'use client';

import useSWR from 'swr';
import { getLocaleInfo } from '@/utils/helpers';
import { StartingKit } from './useStartingKit';
import useLocale from './useLocale';
import { isChildInfoCorrect } from '@/components/homepage-us/utils';
import { ChildInfo } from '@/components/subflow-v3/context/SubFlowV3Context';

export interface AddOns {
  standalone_add_ons: StandaloneAddOn[];
  title: string;
  cta_text: string;
  next_page: string;
  subscription_add_ons: SubscriptionAddOn[];
}

export interface StandaloneAddOn {
  anchor: string;
  variant_id: string;
  name: string;
  price: number;
  image_uri: string;
  description_html?: string;
}

export interface SubscriptionAddOn {
  id: string;
  uuid: string;
  kind: ComponentKind;
  anchor: string;
  parent_product_anchor: string;
  parent_product_uuid: string;
  copy_text: CopyText;
  metadata: SubscriptionAddOnMetadata;
  customizable_options: any[];
  add_ons: any[];
  components: Component[];
  related_products: any[];
  variants: Variant[];
  language: Language;
  media: Media[];
  price?: number;
}

export interface Component {
  id: string;
  uuid: string;
  kind: ComponentKind;
  anchor: string;
  parent_product_anchor: null;
  parent_product_uuid: null;
  copy_text: CopyText;
  metadata: ComponentMetadata;
  customizable_options: any[];
  language: Language;
  media: Media[];
}

export interface CopyText {
  title: string;
  description_html: string;
  skip_copy: null;
  title_tag: null;
  age_tagline: null;
  parent_name: null;
  pricing_copy: null;
  long_description: null;
  rich_snippets_html: null;
}

export enum ComponentKind {
  BooksAddon = 'books_addon',
  Product = 'product',
}

export enum Language {
  En = 'en',
}

export interface Media {
  uuid: string;
  key: Key;
  uri: string;
  alt_text: null;
  kind: MediaKind;
}

export enum Key {
  Gallery = 'gallery',
  Primary = 'primary',
}

export enum MediaKind {
  Image = 'image',
}

export interface ComponentMetadata {
  hidden?: string;
}

export interface SubscriptionAddOnMetadata {
  hidden: string;
  max_age: number;
  min_age: number;
  age_unit: string;
  price_cents: number;
  description_tag?: string;
  // Optional field added for BFCM 2024
  discounted_price?: number;
}

export interface Variant {
  id: string;
  uuid: string;
  external_id: string;
  is_default: boolean;
}

export const useAddOns = (
  childInfo: Partial<ChildInfo>,
  language: string,
  startingKit?: StartingKit,
) => {
  const locale = useLocale();
  const localeInfo = getLocaleInfo(locale);

  const searchParams = new URLSearchParams({
    date_of_birth: `${childInfo.year}-${childInfo.month}-${childInfo.day}`,
    starting_kit: startingKit?.anchor || '',
    selected_language: language,
    language: localeInfo.language,
    currency_code: localeInfo.currencyCode.toLowerCase(),
  });

  const hasFullPaylod =
    isChildInfoCorrect(childInfo) &&
    startingKit?.anchor &&
    searchParams.has('selected_language') &&
    searchParams.has('language') &&
    searchParams.has('currency_code');

  searchParams.sort();

  return useSWR<AddOns>(
    hasFullPaylod
      ? `/api/v1/orchestration_subflow/add_ons?${searchParams}`
      : null,
  );
};
